import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import { daysUntil } from "../../../utils/fechaUtils";
import {
  ClimaHorarioItem,
  ClimaService,
} from "../../../generated/grpc/clima_pb";
import { grpcFincaClimaHorarioDatabase } from "../database/GrpcFincaClimaHorarioDatabase";
import { FincaClimaDataHorarioResponse } from "../response/FincaClimaDataHorarioResponse";

/** Clima horario de una finca (`ClimaService.GetHorarioByFincaId`, gRPC-Web).
 *  Sin delta: el rpc no tiene `ultimaSincronizacion`; el propio mensaje trae
 *  `proximaSincronizacion` y esa fecha decide cuándo volver a pedir (no un
 *  cooldown en memoria). */
class FincaClimaHorarioGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<FincaClimaDataHorarioResponse[]> {
    const cached = await grpcFincaClimaHorarioDatabase.get<ClimaHorarioItem>(
      this._fincaId,
    );
    if (cached && daysUntil(cached.proximaSincronizacion) > 0) {
      return cached.items.map(toFincaClimaDataHorarioResponse);
    }

    const client = createClient(ClimaService, grpcTransportProvider.get());

    let response;
    try {
      response = await client.getHorarioByFincaId({ fincaId: this._fincaId });
    } catch (error) {
      throw mapConnectError(error);
    }

    await grpcFincaClimaHorarioDatabase.put(
      this._fincaId,
      response.proximaSincronizacion,
      response.items,
    );
    return response.items.map(toFincaClimaDataHorarioResponse);
  }

  /** Borra el registro cacheado: fuerza una sincronización en la próxima llamada. */
  clearCache(): void {
    grpcFincaClimaHorarioDatabase.delete(this._fincaId).catch(() => {});
  }
}

/** Factory cacheada: una única instancia por fincaId. */
export const fx_createFincaClimaHorarioGrpcService = createCachedFactory(
  (fincaId: string) => new FincaClimaHorarioGrpcService(fincaId),
);

/** Adapta un `ClimaHorarioItem` (gRPC) al shape `FincaClimaDataHorarioResponse`
 *  (REST). Los 18 campos de meteoblue sin uso real en toda la app (auditado)
 *  se rellenan inertes: `felttemperature`, `wetbulbtemperature`,
 *  `dewpointtemperature`, `skintemperature`, `convectivePrecipitation`,
 *  `snowfraction`, `leafwetnessindex`, `uvindex`, `highclouds`, `midclouds`,
 *  `lowclouds`, `fogProbability`, `visibility`, `sealevelpressure`,
 *  `sensibleheatflux`, `potentialevapotranspiration`,
 *  `referenceevapotranspirationFao`, `pictocode` (ver comentario del proto). */
function toFincaClimaDataHorarioResponse(
  item: ClimaHorarioItem,
): FincaClimaDataHorarioResponse {
  return {
    fecha: item.fecha,
    temperature: item.temperature,
    felttemperature: 0,
    wetbulbtemperature: 0,
    dewpointtemperature: 0,
    skintemperature: 0,
    soiltemperature0to10cm: item.soiltemperature0to10cm,
    relativehumidity: item.relativehumidity,
    soilmoisture0to10cm: item.soilmoisture0to10cm,
    precipitation: item.precipitation,
    convectivePrecipitation: 0,
    precipitationProbability: item.precipitationProbability,
    snowfraction: 0,
    sunshinetime: item.sunshinetime,
    leafwetnessindex: 0,
    uvindex: 0,
    isdaylight: item.isdaylight,
    totalcloudcover: item.totalcloudcover,
    highclouds: 0,
    midclouds: 0,
    lowclouds: 0,
    fogProbability: 0,
    visibility: 0,
    windspeed: item.windspeed,
    winddirection: item.winddirection,
    sealevelpressure: 0,
    sensibleheatflux: 0,
    evapotranspiration: item.evapotranspiration,
    potentialevapotranspiration: 0,
    referenceevapotranspirationFao: 0,
    pictocode: 0,
    rainspot: item.rainspot,
  };
}
